import React, { useMemo } from "react";
import * as THREE from "three";
import { Edges } from "@react-three/drei";

function toNumber(value, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function degToRad(value) {
  return (toNumber(value, 0) * Math.PI) / 180;
}

function makeMaterial({
  color = "#d1d5db",
  metalness = 0.15,
  roughness = 0.55,
  opacity = 1,
  wireframe = false,
} = {}) {
  return new THREE.MeshStandardMaterial({
    color,
    metalness,
    roughness,
    transparent: opacity < 1 || wireframe,
    opacity: wireframe ? 0.08 : opacity,
    depthWrite: !wireframe,
    side: THREE.DoubleSide,
  });
}

function normalizeGeometryInput(input) {
  if (!input) return null;

  // Podržava oba oblika:
  // metadata_json.geometry
  // metadata_json = { geometry: {...} }
  if (input.geometry) return input.geometry;

  return input;
}

function getParams(geometry = {}) {
  return geometry.parameters || geometry.params || {};
}

function createBoxGeometry(geometry) {
  const p = getParams(geometry);

  const width = toNumber(p.width ?? geometry.width, 50);
  const height = toNumber(p.height ?? geometry.height, 50);
  const depth = toNumber(p.depth ?? geometry.depth, 50);

  return new THREE.BoxGeometry(width, height, depth);
}

function createCylinderGeometry(geometry) {
  const p = getParams(geometry);

  const diameter = toNumber(p.diameter ?? geometry.diameter, 10);
  const radius = toNumber(p.radius, diameter / 2);
  const length = toNumber(p.length ?? p.height ?? geometry.length, 30);
  const segments = Math.max(12, toNumber(p.segments ?? geometry.segments, 48));

  const g = new THREE.CylinderGeometry(radius, radius, length, segments);

  // Default cylinder osa je Y. Za šrafove/okove nam je često zgodno da dužina ide po X.
  if ((geometry.axis || p.axis || "x") === "x") {
    g.rotateZ(Math.PI / 2);
  }

  if ((geometry.axis || p.axis) === "z") {
    g.rotateX(Math.PI / 2);
  }

  return g;
}

function createConeGeometry(geometry) {
  const p = getParams(geometry);

  const radiusTop = toNumber(p.radiusTop ?? p.topRadius, 0);
  const radiusBottom = toNumber(
    p.radiusBottom ?? p.bottomRadius ?? p.radius ?? p.diameter / 2,
    5
  );
  const length = toNumber(p.length ?? p.height, 20);
  const segments = Math.max(12, toNumber(p.segments, 48));

  const g = new THREE.ConeGeometry(radiusBottom, length, segments);

  // ConeGeometry nema radiusTop, za MVP koristimo pravi konus.
  if ((geometry.axis || p.axis || "x") === "x") {
    g.rotateZ(Math.PI / 2);
  }

  if ((geometry.axis || p.axis) === "z") {
    g.rotateX(Math.PI / 2);
  }

  return g;
}

function createSphereGeometry(geometry) {
  const p = getParams(geometry);

  const diameter = toNumber(p.diameter ?? geometry.diameter, 20);
  const radius = toNumber(p.radius, diameter / 2);
  const segments = Math.max(12, toNumber(p.segments, 48));

  return new THREE.SphereGeometry(radius, segments, Math.max(8, segments / 2));
}

function createScrewGeometry(geometry) {
  const p = getParams(geometry);

  const diameter = toNumber(p.diameter, 4);
  const length = toNumber(p.length, 16);

  const headDiameter = toNumber(
    p.headDiameter ?? p.head_diameter,
    diameter * 2
  );

  const headHeight = toNumber(
    p.headHeight ?? p.head_height,
    diameter * 0.7
  );

  const tipLength = toNumber(
    p.tipLength ?? p.tip_length,
    Math.min(diameter * 1.0, length * 0.2)
  );

  const headStyle = String(
    p.headStyle ?? p.head_style ?? "countersunk"
  ).toLowerCase();

  const segments = Math.max(24, toNumber(p.segments, 64));

  const bodyLength = Math.max(0.1, length - headHeight - tipLength);
  const bodyRadius = diameter / 2;
  const headRadius = headDiameter / 2;

  const color = geometry.color || "#9ca3af";
  const headColor = geometry.headColor || geometry.color || "#6b7280";

  const metal = makeMaterial({
    color,
    metalness: 0.35,
    roughness: 0.35,
  });

  const headMaterial = makeMaterial({
    color: headColor,
    metalness: 0.4,
    roughness: 0.3,
  });

  const darkMaterial = makeMaterial({
    color: "#111827",
    metalness: 0.1,
    roughness: 0.65,
  });

  const group = new THREE.Group();

  // X osa = dužina šrafa
  const left = -length / 2;

  const tipCenterX = left + tipLength / 2;
  const bodyCenterX = left + tipLength + bodyLength / 2;
  const headCenterX = left + tipLength + bodyLength + headHeight / 2;
  const frontFaceX = length / 2;

  // Vrh
  const tip = new THREE.Mesh(
    new THREE.ConeGeometry(bodyRadius, tipLength, segments),
    metal
  );
  tip.rotation.z = Math.PI / 2;
  tip.position.x = tipCenterX;
  group.add(tip);

  // Telo
  const body = new THREE.Mesh(
    new THREE.CylinderGeometry(bodyRadius, bodyRadius, bodyLength, segments),
    metal
  );
  body.rotation.z = Math.PI / 2;
  body.position.x = bodyCenterX;
  group.add(body);

  // Glava
  let headGeo;

  if (headStyle === "countersunk" || headStyle === "flat") {
    // Konusna / upuštena glava:
    // levo (ka telu) = bodyRadius
    // desno (spoljna ravna strana) = headRadius
    headGeo = new THREE.CylinderGeometry(
      bodyRadius,
      headRadius,
      headHeight,
      segments
    );
  } else {
    // obična cilindrična glava
    headGeo = new THREE.CylinderGeometry(
      headRadius,
      headRadius,
      headHeight,
      segments
    );
  }

  const head = new THREE.Mesh(headGeo, headMaterial);
  head.rotation.z = Math.PI / 2;
  head.position.x = headCenterX;
  group.add(head);

  // Prorez / krst na glavi
  const slotType = String(p.slotType || p.slot_type || "slot").toLowerCase();

  const slotDepth = 0.8;
  const slotWidth = Math.max(0.35, headDiameter * 0.08);
  const slotLength = headDiameter * 0.72;

  const slot1 = new THREE.Mesh(
    new THREE.BoxGeometry(slotDepth, slotLength, slotWidth),
    darkMaterial
  );
  slot1.position.set(frontFaceX + 0.05, 0, 0);
  group.add(slot1);

  if (slotType === "phillips" || slotType === "cross") {
    const slot2 = new THREE.Mesh(
      new THREE.BoxGeometry(slotDepth, slotWidth, slotLength),
      darkMaterial
    );
    slot2.position.set(frontFaceX + 0.06, 0, 0);
    group.add(slot2);
  }

  return group;
}

function createDowelGeometry(geometry) {
  const p = getParams(geometry);

  return createCylinderGeometry({
    ...geometry,
    axis: p.axis || geometry.axis || "x",
    parameters: {
      diameter: p.diameter ?? 8,
      length: p.length ?? 30,
      segments: p.segments ?? 48,
    },
  });
}

function createHandleGeometry(geometry) {
  const p = getParams(geometry);

  const length = toNumber(p.length, 160);
  const height = toNumber(p.height, 28);
  const depth = toNumber(p.depth, 35);
  const barDiameter = toNumber(p.barDiameter ?? p.bar_diameter, 10);
  const legDiameter = toNumber(p.legDiameter ?? p.leg_diameter, 8);
  const holeDistance = toNumber(p.holeDistance ?? p.hole_distance, 128);

  const group = new THREE.Group();
  const material = makeMaterial({
    color: geometry.color || "#6b7280",
    metalness: 0.35,
    roughness: 0.32,
  });

  const bar = new THREE.Mesh(
    new THREE.CylinderGeometry(barDiameter / 2, barDiameter / 2, length, 48),
    material
  );
  bar.rotation.z = Math.PI / 2;
  bar.position.z = depth;
  group.add(bar);

  [-holeDistance / 2, holeDistance / 2].forEach((x) => {
    const leg = new THREE.Mesh(
      new THREE.CylinderGeometry(legDiameter / 2, legDiameter / 2, depth, 32),
      material
    );
    leg.rotation.x = Math.PI / 2;
    leg.position.set(x, 0, depth / 2);
    group.add(leg);
  });

  // Tanka montažna pločica između daske i ručke.
// Leži na XY ravni, od Z=0 do Z=basePlateThickness.
const basePlateThickness = toNumber(
  p.basePlateThickness ?? p.plateThickness ?? geometry.basePlateThickness,
  2
);

const basePlateLength = toNumber(
  p.basePlateLength ?? p.plateLength ?? geometry.basePlateLength,
  length
);

const basePlateHeight = toNumber(
  p.basePlateHeight ?? p.plateHeight ?? geometry.basePlateHeight,
  Math.max(barDiameter * 1.6, legDiameter * 2)
);

const basePlate = new THREE.Mesh(
  new THREE.BoxGeometry(basePlateLength, basePlateHeight, basePlateThickness),
  makeMaterial({
    color: geometry.plateColor || geometry.color || "#6b7280",
    metalness: 0.25,
    roughness: 0.42,
    opacity: 1,
  })
);

basePlate.position.z = basePlateThickness / 2;
group.add(basePlate);

// Nevidljiva zona za lakše selektovanje.
// Ona ostaje velika, ali se ne renderuje.
const hitBox = new THREE.Mesh(
  new THREE.BoxGeometry(length, height, Math.max(depth, barDiameter)),
  new THREE.MeshBasicMaterial({
    transparent: true,
    opacity: 0,
    depthWrite: false,
    colorWrite: false,
  })
);

hitBox.position.z = depth / 2;
hitBox.userData.isHitBox = true;
group.add(hitBox);

  return group;
}

function createExtrudeGeometry(geometry) {
  const profile = geometry.profile || {};
  const points = Array.isArray(profile.points)
    ? profile.points
    : Array.isArray(geometry.points)
    ? geometry.points
    : [];

  if (points.length < 3) return null;

  const shape = new THREE.Shape();
  points.forEach((pt, index) => {
    const x = toNumber(pt.x, 0);
    const y = toNumber(pt.y, 0);

    if (index === 0) shape.moveTo(x, y);
    else shape.lineTo(x, y);
  });

  shape.closePath();

  const depth = toNumber(geometry.depth ?? profile.depth, 10);

  const g = new THREE.ExtrudeGeometry(shape, {
    depth,
    bevelEnabled: Boolean(geometry.bevelEnabled),
    bevelThickness: toNumber(geometry.bevelThickness, 0.5),
    bevelSize: toNumber(geometry.bevelSize, 0.5),
    bevelSegments: toNumber(geometry.bevelSegments, 2),
  });

  g.center();
  return g;
}

function createRevolveGeometry(geometry) {
  const profile = geometry.profile || {};
  const points = Array.isArray(profile.points)
    ? profile.points
    : Array.isArray(geometry.points)
    ? geometry.points
    : [];

  if (points.length < 2) return null;

  const vectors = points.map(
    (pt) => new THREE.Vector2(toNumber(pt.x, 0), toNumber(pt.y, 0))
  );

  const segments = Math.max(16, toNumber(geometry.segments ?? profile.segments, 64));
  const g = new THREE.LatheGeometry(vectors, segments);

  const axis = geometry.axis || profile.axis || "y";

  // Lathe se vrti oko Y ose. Po potrebi rotiramo za prikaz.
  if (axis === "x") {
    g.rotateZ(Math.PI / 2);
  }

  if (axis === "z") {
    g.rotateX(Math.PI / 2);
  }

  g.center();
  return g;
}

function buildGeneratedObject(geometry) {
  const method = String(geometry.method || geometry.type || "box").toLowerCase();
  const type = String(geometry.type || geometry.kind || method).toLowerCase();

  if (type === "screw") return createScrewGeometry(geometry);
  if (type === "dowel") {
    const g = createDowelGeometry(geometry);
    return g ? new THREE.Mesh(g, makeMaterial({ color: geometry.color || "#d1d5db" })) : null;
  }
  if (type === "handle") return createHandleGeometry(geometry);

  let g = null;

  if (method === "primitive" || ["box", "cylinder", "cone", "sphere"].includes(type)) {
    if (type === "box") g = createBoxGeometry(geometry);
    else if (type === "cylinder") g = createCylinderGeometry(geometry);
    else if (type === "cone") g = createConeGeometry(geometry);
    else if (type === "sphere") g = createSphereGeometry(geometry);
  }

  if (method === "extrude" || type === "extrude" || type === "custom_extrude") {
    g = createExtrudeGeometry(geometry);
  }

  if (method === "revolve" || method === "lathe" || type === "revolve") {
    g = createRevolveGeometry(geometry);
  }

  if (!g) return null;

  g.computeVertexNormals();
  g.computeBoundingBox();
  g.computeBoundingSphere();

  return new THREE.Mesh(
    g,
    makeMaterial({
      color: geometry.color || "#d1d5db",
      metalness: toNumber(geometry.metalness, 0.15),
      roughness: toNumber(geometry.roughness, 0.55),
      opacity: toNumber(geometry.opacity, 1),
    })
  );
}

export default function GeneratedAssetMesh({
  geometry,
  metadata,
  position = [0, 0, 0],
  rotation = [0, 0, 0],
  scale = [1, 1, 1],
  selected = false,
  showAxes = false,
  wireframe = false,
  onClick,
}) {
  const normalized = normalizeGeometryInput(geometry || metadata);

  const object = useMemo(() => {
    if (!normalized) return null;

    const obj = buildGeneratedObject(normalized);
    if (!obj) return null;

    obj.traverse?.((child) => {
  if (!child.isMesh) return;

  if (child.userData?.isHitBox) {
    child.visible = false;
    child.raycast = () => {};
    return;
  }

  child.castShadow = true;
  child.receiveShadow = true;

  child.material = makeMaterial({
    color: normalized.color || "#d1d5db",
    metalness: toNumber(normalized.metalness, 0.15),
    roughness: toNumber(normalized.roughness, 0.55),
    opacity: toNumber(normalized.opacity, 1),
    wireframe,
  });

  if (wireframe && child.geometry) {
    const edgesGeometry = new THREE.EdgesGeometry(child.geometry, 1);
    const edgesMaterial = new THREE.LineBasicMaterial({
      color: "#334155",
      transparent: true,
      opacity: 0.9,
      depthTest: true,
    });

    const edges = new THREE.LineSegments(edgesGeometry, edgesMaterial);
    edges.name = "clean_edges_wireframe";
    edges.renderOrder = 2;

    child.add(edges);
  }
});

    return obj;
  }, [JSON.stringify(normalized), wireframe]);

  if (!object) return null;

  const safeRotation = [
    Array.isArray(rotation) ? rotation[0] || 0 : degToRad(rotation.x),
    Array.isArray(rotation) ? rotation[1] || 0 : degToRad(rotation.y),
    Array.isArray(rotation) ? rotation[2] || 0 : degToRad(rotation.z),
  ];

  return (
    <group position={position} rotation={safeRotation} scale={scale} onClick={onClick}>
      <primitive object={object} />

      {selected ? (
        <group>
          <primitive object={object.clone()} visible={false} />
          <mesh>
            <boxGeometry args={[1, 1, 1]} />
            <meshBasicMaterial transparent opacity={0} />
            <Edges color="#2563eb" />
          </mesh>
        </group>
      ) : null}

      {showAxes ? <axesHelper args={[80]} /> : null}
    </group>
  );
}